/**
 * Research Intake (форма запроса) — E06-F01 shared contract.
 *
 * The intake form is the entry point of Stage-2 Company Research: a recruiter
 * fills the full criteria form (Client / Targeting / Size & type / Reference &
 * exclusions / Grades / Verification / Brief) and submits it, which creates a
 * `research_requests` row with `status='draft'` (research_create, T02). Stage 2
 * was NEVER deployed to the legacy n8n prod, so the canonical shape here is the
 * intake JSONB documented in migration 023 + the E06-F01 task spec (status.yaml)
 * — there is no live n8n node to copy verbatim. The AI-paste accelerator
 * (research_criteria_normalize, T04) reads its prompt body ONLY from `ai_prompts`
 * (prompts-only-from-DB; no built-in fallback).
 *
 * This file is feature-owned. It is re-exported from `@eer/shared` by the serial
 * Wire pass — it MUST NOT edit the shared barrel during parallel work.
 *
 * Shared by api (the create/normalize services + routes) and web (the typed
 * api-client + the Intake form). The shapes here are the single source of truth.
 */
import { z } from 'zod';
// `GRADE_SCOPES`/`gradeScopeSchema`/`GradeScope` and `VERIFY_STRICTNESS`/
// `verifyStrictnessSchema`/`VerifyStrictness` are byte-identical to the canonical
// definitions in `jsonb.ts` (the research_requests.criteria blob contract). Re-export
// them from there so there is ONE definition (no TS2308 in the barrel).
import { GRADE_SCOPES, gradeScopeSchema, VERIFY_STRICTNESS, verifyStrictnessSchema, } from '../jsonb.js';
export { GRADE_SCOPES, gradeScopeSchema, VERIFY_STRICTNESS, verifyStrictnessSchema };
// ---------------------------------------------------------------------------
// Enums / vocab (the intake controls)
// ---------------------------------------------------------------------------
/**
 * Intake purpose vocab. DISTINCT from the legacy-blob `RESEARCH_PURPOSES` in
 * `jsonb.ts` (which uses the display-cased `'Sourcing'`/`'Market Mapping'`): the
 * E06 intake FORM persists machine values (`'sourcing'`/`'market_mapping'`), so
 * this is a SEPARATE vocab with its own name — the collision is resolved by
 * renaming, NOT by force-merging two different value sets.
 * Sourcing = ranked long-list (relevance-first). Market Mapping = exhaustive.
 */
export const INTAKE_RESEARCH_PURPOSES = ['sourcing', 'market_mapping'];
export const intakeResearchPurposeSchema = z.enum(INTAKE_RESEARCH_PURPOSES);
/** research_requests.review_mode — gate (mandatory human review) | auto. */
export const RESEARCH_REVIEW_MODES = ['gate', 'auto'];
export const researchReviewModeSchema = z.enum(RESEARCH_REVIEW_MODES);
/** research_requests.status (schema.prisma:402, migration 023). */
export const RESEARCH_REQUEST_STATUSES = [
    'draft',
    'queued',
    'running',
    'in_review',
    'published',
    'failed',
    'cancelled',
];
export const researchRequestStatusSchema = z.enum(RESEARCH_REQUEST_STATUSES);
/**
 * open-by-status routing (T03): the SPA routes the read result by request.status.
 *   draft → intake; running/queued/failed → run; in_review/published → review.
 */
export const RESEARCH_STATUS_VIEW = {
    draft: 'intake',
    queued: 'run',
    running: 'run',
    failed: 'run',
    cancelled: 'run',
    in_review: 'review',
    published: 'review',
};
/** Min / max for the target-count slider (T05, 20..200). */
export const TARGET_COUNT_MIN = 20;
export const TARGET_COUNT_MAX = 200;
export const TARGET_COUNT_DEFAULT = 100;
// ---------------------------------------------------------------------------
// Resolved location (geo tile) — each location → {label,lat,lng,radius_km}
// ---------------------------------------------------------------------------
/**
 * A resolved location: city searches carry a metro `radiusKm`; whole-country
 * searches omit the radius (T04 acceptance: "whole-country → radius отсутствует").
 * Always stored in kilometers (the intake UI converts any mi-entered value to km
 * before persisting) — see decision-log ADR "radiusMi→radiusKm".
 */
export const intakeLocationSchema = z.object({
    label: z.string().min(1),
    lat: z.number().nullable().default(null),
    lng: z.number().nullable().default(null),
    /** Metro radius in kilometers (city search only); null/omitted for whole-country. */
    radiusKm: z.number().positive().nullable().optional(),
    /**
     * E06-F01-T10 — the location's search stage ("Europe first, then Global"):
     * 1 = searched first, 2 = only when stage 1 did not reach the target, … Omitted =
     * stage 1 (every pre-T10 location). Discovery runs the stages in order.
     */
    stage: z.number().int().min(1).max(5).optional(),
});
/** E06-F01-T10 — how many location stages the intake offers. */
export const LOCATION_STAGES_MAX = 5;
// ---------------------------------------------------------------------------
// Grade definitions (verbatim from the recruiter's brief)
// ---------------------------------------------------------------------------
/** Grade A/B/C definitions — copied verbatim into the criteria (T04 acceptance). */
export const gradeDefinitionsSchema = z.object({
    a: z.string().default(''),
    b: z.string().default(''),
    c: z.string().default(''),
});
// ---------------------------------------------------------------------------
// Soft criterion (weighted) — the SOFT side of the HARD/SOFT split (T04)
// ---------------------------------------------------------------------------
/** One weighted SOFT criterion (industry fit, services match, …). */
export const softCriterionSchema = z.object({
    key: z.string().min(1),
    label: z.string().default(''),
    weight: z.number().min(0).max(100).default(0),
});
// ---------------------------------------------------------------------------
// The full intake criteria (research_requests.criteria JSONB)
// ---------------------------------------------------------------------------
/**
 * The full intake form payload — every section of the New Research Request form.
 * This is the shape persisted into `research_requests.criteria` and consumed by
 * the per-company pipeline (E06-F03). All collection fields default to [] so a
 * partially-filled draft round-trips cleanly.
 */
export const intakeCriteriaSchema = z.object({
    // --- Client ---
    /**
     * Resolved existing-client id, or null. A positive id points at an already
     * persisted `clients` row (picked via the client autocomplete). null (or
     * omitted) means "no existing client was resolved" — the API create step is
     * expected to mint a NEW client from `clientName`. Matches the nullable-optional
     * id convention of `researchCreateInputSchema.projectId` (this file) and the
     * `ClientOption.id` shape (project-detail-card.ts).
     */
    clientId: z.number().int().positive().nullable().optional(),
    clientName: z.string().default(''),
    clientWebsite: z.string().default(''),
    clientAbout: z.string().default(''),
    /** "Add client to Do-Not-Source" — checked by default (T05 acceptance). */
    addClientToDns: z.boolean().default(true),
    // --- Targeting ---
    /** REQUIRED (research_create rejects an empty industry — T02 acceptance). */
    industry: z.string().default(''),
    subCategories: z.array(z.string()).default([]),
    /** REQUIRED: at least one location (research_create rejects empty — T02). */
    locations: z.array(intakeLocationSchema).default([]),
    foundedFrom: z.number().int().nullable().default(null),
    foundedTo: z.number().int().nullable().default(null),
    /** Metro radius units the UI showed (mi|km); the value lives per-location. */
    metroUnit: z.enum(['mi', 'km']).default('mi'),
    /**
     * E06-F01-T10 — countries / regions excluded from EVERY location stage ("UK" in
     * "Europe first, then Global excl. UK"). Applied as an exclusion clause on each
     * location (researchLocationResolve, E07-F02-T20).
     */
    locationExclusions: z.array(z.string()).default([]),
    // --- Size & type ---
    sizeGlobal: z.string().default(''),
    /** Local-headcount cap (HARD filter). */
    sizeLocalCap: z.number().int().nullable().default(null),
    /** Multi-select company-type pills + custom (T07). */
    companyTypes: z.array(z.string()).default([]),
    /** Multi-select funding pills + custom (T07). */
    fundingStages: z.array(z.string()).default([]),
    // --- Reference & exclusions ---
    competitors: z.array(z.string()).default([]),
    dnsCompanies: z.array(z.string()).default([]),
    dnsIndustries: z.array(z.string()).default([]),
    /**
     * E06-F01-T10 — the brief's "Avoid" line: business models not to source
     * ("distributors", "integrators", "pure SaaS", "only B2C"). Own field (it used to
     * be squeezed into dnsIndustries / freeText): cut at discovery by the provider
     * industry (reason `avoid`) and passed to the AI check / scoring prompts.
     */
    avoid: z.array(z.string()).default([]),
    /**
     * E07-F07-T03 — «Exclude EER clients (global list)». Absent = ON (spec default
     * `true`); only an explicit `false` switches the global Do-Not-Source list off.
     * Optional (not `.default(true)`) so existing fully-typed intake literals stay valid.
     */
    useGlobalDns: z.boolean().optional(),
    /**
     * E07-F07-T03 — SERVER-WRITTEN snapshot of the global Do-Not-Source list (active
     * entries' names / domains), copied in by research_start (researchJobCommands) so
     * the run is reproducible. Rewritten on every start ([] when `useGlobalDns` is
     * false); a value sent by a client is overwritten. Never sent to an AI prompt.
     */
    dnsGlobal: z.array(z.string()).optional(),
    // --- Grades / scope / purpose (T06) ---
    purpose: intakeResearchPurposeSchema.default('sourcing'),
    gradeScope: gradeScopeSchema.default('A+B'),
    gradeDefinitions: gradeDefinitionsSchema.default({ a: '', b: '', c: '' }),
    // --- Verification (T07) ---
    verifyStrictness: verifyStrictnessSchema.default('strict'),
    // --- Brief ---
    freeText: z.string().default(''),
    linkedinUrl: z.string().default(''),
    // --- Discovery sizing ---
    targetCount: z
        .number()
        .int()
        .min(TARGET_COUNT_MIN)
        .max(TARGET_COUNT_MAX)
        .default(TARGET_COUNT_DEFAULT),
    // --- Soft criteria (weighted) — populated by the normalize accelerator (T04) ---
    softCriteria: z.array(softCriterionSchema).default([]),
});
// ---------------------------------------------------------------------------
// research_create (T02) — request / response
// ---------------------------------------------------------------------------
/**
 * research_create input. `owner_id`/`user_id` are DELIBERATELY ABSENT — the owner
 * is taken ONLY from the session (IDOR rule, T02 acceptance). `force_new` bypasses
 * the criteria_hash idempotency window.
 */
export const researchCreateInputSchema = z.object({
    projectId: z.number().int().positive().nullable().optional(),
    title: z.string().default(''),
    criteria: intakeCriteriaSchema,
    reviewMode: researchReviewModeSchema.optional(),
    forceNew: z.boolean().default(false),
});
/** A draft request as returned to the SPA after create. */
export const researchRequestDraftSchema = z.object({
    id: z.number().int().positive(),
    projectId: z.number().int().positive().nullable(),
    ownerId: z.string().nullable(),
    title: z.string(),
    status: researchRequestStatusSchema,
    criteriaHash: z.string(),
    reviewMode: researchReviewModeSchema,
    targetCount: z.number().int().nullable(),
});
/** research_create success body (T02). `conflict` flags a re-used existing draft. */
export const researchCreateResponseSchema = z.object({
    ok: z.literal(true),
    request: researchRequestDraftSchema,
    /** true when an existing draft was returned by criteria_hash dedup (no force_new). */
    conflict: z.boolean().default(false),
});
/**
 * E12-F17-T07 — research_update_draft input (PATCH /research/requests/:id). A draft
 * opened from History is edited IN PLACE: title / criteria / linked project are
 * replaced on that row (criteria_hash recomputed), so Save draft / Run from an
 * opened draft never mints a duplicate. Only a `draft` row may be updated.
 * Response: the same `researchCreateResponseSchema` body (`conflict: false`).
 */
export const researchUpdateDraftInputSchema = z.object({
    projectId: z.number().int().positive().nullable().optional(),
    title: z.string().default(''),
    criteria: intakeCriteriaSchema,
});
/**
 * E12-F17-T07 — the 409 "A draft with identical criteria already exists" carries the
 * existing draft under `error.details.request`; this reads its id (null otherwise),
 * so the SPA can offer "Open the existing draft" instead of a dead end.
 */
export function conflictingDraftId(details) {
    if (!details || typeof details !== 'object')
        return null;
    const req = details.request;
    if (!req || typeof req !== 'object')
        return null;
    const id = req.id;
    return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}
// ---------------------------------------------------------------------------
// research_get (T03) — read a request (open-by-status)
// ---------------------------------------------------------------------------
/** A long-list company row as projected into research_get (minimal, T03). */
export const researchGetCompanyRowSchema = z.object({
    id: z.number(),
    companyId: z.number().nullable(),
    rawName: z.string().nullable(),
    stage: z.string(),
    grade: z.string().nullable(),
    score: z.number().nullable(),
});
/** A job row (orchestrator) as projected into research_get. */
export const researchGetJobSchema = z.object({
    id: z.number(),
    status: z.string(),
    currentStage: z.string().nullable(),
    totalCompanies: z.number(),
    completedCompanies: z.number(),
});
/** research_get success body: { request, job|null, companies[] } (T03). */
export const researchGetResponseSchema = z.object({
    ok: z.literal(true),
    request: z.object({
        id: z.number(),
        projectId: z.number().nullable(),
        ownerId: z.string().nullable(),
        title: z.string(),
        status: researchRequestStatusSchema,
        criteria: intakeCriteriaSchema,
        criteriaHash: z.string(),
        reviewMode: researchReviewModeSchema,
        targetCount: z.number().nullable(),
    }),
    job: researchGetJobSchema.nullable(),
    companies: z.array(researchGetCompanyRowSchema),
});
// ---------------------------------------------------------------------------
// research_criteria_normalize (T04) — AI-paste accelerator
// ---------------------------------------------------------------------------
/** The prompt key the accelerator loads from `ai_prompts` (DB only, no fallback). */
export const RESEARCH_CRITERIA_NORMALIZE_PROMPT_KEY = 'research_criteria_normalize';
/** research_criteria_normalize input: the raw pasted recruiter brief + any seed. */
export const criteriaNormalizeInputSchema = z.object({
    /** The pasted email / Word text (Client/Industry/Sub-category/Location/…). */
    rawText: z.string().default(''),
    /** Optional already-typed intake fields to merge with the AI extraction. */
    rawIntake: intakeCriteriaSchema.partial().optional(),
});
/**
 * research_criteria_normalize output — the structured decomposition the prompt
 * returns: HARD filters (geo radius / size cap) separated from SOFT (weighted)
 * criteria, resolved geo tiles, verbatim grade definitions, DNS lists, etc.
 *
 * E06-F01-T09 — the contract additionally carries the PLAIN INTAKE FIELDS
 * (`industry`, `subCategories`, `roleTitle`, client block, size / company-type /
 * funding). Until T09 the result had NO slot for them at all, so `parse()` dropped
 * whatever the AI extracted and the form could only ever autofill the location —
 * exactly the reported bug. Every new field is blank-by-default: the AI leaves it
 * empty when the brief says nothing, and the form merges non-clobbering (a value
 * the recruiter already typed always wins).
 */
export const criteriaNormalizeResultSchema = z.object({
    hardFilters: z
        .object({
        geo: z
            .object({
            locations: z.array(z.string()).default([]),
            radiusKm: z.number().nullable().default(null),
        })
            .default({ locations: [], radiusKm: null }),
        sizeLocalCap: z.number().int().nullable().default(null),
        foundedFrom: z.number().int().nullable().default(null),
        foundedTo: z.number().int().nullable().default(null),
    })
        .default({
        geo: { locations: [], radiusKm: null },
        sizeLocalCap: null,
        foundedFrom: null,
        foundedTo: null,
    }),
    softCriteria: z.array(softCriterionSchema).default([]),
    geoTiles: z.array(intakeLocationSchema).default([]),
    gradeDefinitions: gradeDefinitionsSchema.default({ a: '', b: '', c: '' }),
    gradeScope: gradeScopeSchema.default('A+B'),
    dnsCompanies: z.array(z.string()).default([]),
    dnsIndustries: z.array(z.string()).default([]),
    competitors: z.array(z.string()).default([]),
    targetCount: z
        .number()
        .int()
        .min(TARGET_COUNT_MIN)
        .max(TARGET_COUNT_MAX)
        .default(TARGET_COUNT_DEFAULT),
    verifyStrictness: verifyStrictnessSchema.default('strict'),
    // --- Plain intake-form fields (E06-F01-T09) — '' / [] when the brief is silent ---
    /** The hiring client's company name (the brief's "Client:"), '' when absent. */
    clientName: z.string().default(''),
    clientWebsite: z.string().default(''),
    clientAbout: z.string().default(''),
    /** The ONE primary industry of the TARGET companies (the required intake field). */
    industry: z.string().default(''),
    /** Narrower sub-industries / niches named or implied by the brief. */
    subCategories: z.array(z.string()).default([]),
    /** Company-size band ('', '1–50', '50–500', '500–1000', '1000+'); snapped by the form. */
    sizeGlobal: z.string().default(''),
    companyTypes: z.array(z.string()).default([]),
    fundingStages: z.array(z.string()).default([]),
    /**
     * The POSITION the client is recruiting for ("Business Development Manager").
     * The intake has no role field of its own — the form seeds the Request title
     * with it (T09 acceptance: "локация, индустрия и роль").
     */
    roleTitle: z.string().default(''),
    /**
     * Research purpose when the brief states one; null = "the AI could not tell",
     * which keeps the recruiter's toggle untouched (an unconditional 'sourcing'
     * default would silently clobber a Market-Mapping choice).
     */
    purpose: intakeResearchPurposeSchema.nullable().default(null),
    /**
     * E06-F01-T12 — places the brief excludes ("Do-Not-Source States: California,
     * New York"). Lands in the intake's `locationExclusions` (Excluding locations).
     * [] when the brief excludes nothing.
     */
    locationExclusions: z.array(z.string()).default([]),
});
/** research_criteria_normalize success body (T04). `warning` set when input empty. */
export const criteriaNormalizeResponseSchema = z.object({
    ok: z.boolean(),
    result: criteriaNormalizeResultSchema.nullable(),
    /** Non-null when the AI was NOT called (empty input or parse failure). */
    warning: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// E06-F01-T14 — project people shown on the Intake + Run screens
// ---------------------------------------------------------------------------
/** One person shown next to the linked project (name + email; id when a user). */
export const researchProjectPersonSchema = z.object({
    id: z.string().nullable().default(null),
    name: z.string(),
    email: z.string().default(''),
});
/**
 * GET /research/projects/:projectId/people — who the research is for:
 *   - `recruiter`          — the project owner (projects.owner_id → users);
 *   - `recruitingTeamLead` — the teamlead of the recruiter's team
 *                            (users.team_id = teams.name → teams.teamlead_id);
 *   - `sourcingTeam`       — the sourcing team assigned to the project
 *                            (projects.sourcing_team_id) with its members.
 * Every part is nullable — a project may have none of them assigned.
 */
export const researchProjectPeopleResponseSchema = z.object({
    ok: z.literal(true),
    projectId: z.number().int().positive(),
    projectLabel: z.string(),
    recruiter: researchProjectPersonSchema.nullable(),
    recruitingTeamLead: researchProjectPersonSchema.nullable(),
    sourcingTeam: z
        .object({
        id: z.number().int(),
        name: z.string(),
        members: z.array(researchProjectPersonSchema),
    })
        .nullable(),
});
//# sourceMappingURL=research-intake.js.map